import React from "react";
import PropTypes from "prop-types";
import { buildScopeTranslate } from "lib/I18n";
import { refPropType } from "./utils";

export const DEFAULT_MIN_CHARS = 12;
export const DEFAULT_MAX_CHARS = 2000;
// Announce remaining characters only near the end of this limit. Five percent
// matches the old 100-character window at the 2000 default. The floor keeps a
// short limit from warning on a single character, and the quarter cap keeps
// that floor from covering the whole answer.
const NEAR_LIMIT_RATIO = 0.05;
const NEAR_LIMIT_FLOOR = 10;
const NEAR_LIMIT_MAX_SHARE = 0.25;

function nearLimitThreshold(maxChars) {
  const scaled = Math.ceil(maxChars * NEAR_LIMIT_RATIO);
  const withFloor = Math.max(NEAR_LIMIT_FLOOR, scaled);
  return Math.min(withFloor, Math.ceil(maxChars * NEAR_LIMIT_MAX_SHARE));
}

const tQuestion = buildScopeTranslate("views.content.assessment_framework.assessments.pages.question");

const SaqInput = ({
  questionId,
  questionPrompt = "",
  value = "",
  onChange,
  disabled = false,
  minChars = DEFAULT_MIN_CHARS,
  maxChars = DEFAULT_MAX_CHARS,
  inputRef = null,
}) => {
  const textareaId = `assessment-framework-question-${questionId}`;
  const charCounterId = `${textareaId}-char-counter`;
  const overLimitId = `${textareaId}-over-limit`;
  const minHintId = `${textareaId}-min-hint`;
  const remaining = maxChars - value.length;
  const isOverLimit = remaining < 0;
  const isApproachingLimit = remaining >= 0 && remaining <= nearLimitThreshold(maxChars);
  const describedBy = [minHintId, isOverLimit ? overLimitId : null, charCounterId]
    .filter(Boolean)
    .join(" ");
  const counterClassName = isOverLimit
    ? "th-agentforce-assessment__saq-input-over-limit"
    : "th-agentforce-assessment__saq-input-counter";

  return (
    <div className="th-agentforce-assessment__saq-input">
      <label className="th-sr-only" htmlFor={textareaId}>{questionPrompt}</label>
      <p id={minHintId} className="th-agentforce-assessment__saq-input-paragraph">
        {tQuestion("min_chars_hint", { count: minChars })}
      </p>
      <div className="th-agentforce-assessment__saq-input-section">
        <textarea
          ref={inputRef}
          id={textareaId}
          name={textareaId}
          placeholder={tQuestion("answer_placeholder")}
          className="th-agentforce-assessment__saq-input-textarea"
          value={value}
          onChange={onChange}
          disabled={disabled}
          rows={4}
          aria-invalid={isOverLimit}
          aria-describedby={describedBy} />
        <div className="th-agentforce-assessment__saq-input-footer">
          {isOverLimit && (
            <p
              id={overLimitId}
              className="th-agentforce-assessment__saq-input-over-limit"
              data-testid="saq-over-limit"
              role="alert">
              {tQuestion("over_character_limit")}
            </p>
          )}
          <p
            id={charCounterId}
            className={counterClassName}
            data-testid="saq-char-counter">
            {`${value.length}/${maxChars}`}
          </p>
          {isApproachingLimit && (
            <span aria-live="polite" className="th-sr-only" data-testid="saq-chars-remaining">
              {tQuestion("chars_remaining", { count: remaining })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

SaqInput.propTypes = {
  questionId: PropTypes.string.isRequired,
  questionPrompt: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  minChars: PropTypes.number,
  maxChars: PropTypes.number,
  inputRef: refPropType,
};

export default SaqInput;
